import { useReducer, useEffect, useState, ChangeEvent } from "react";

import * as StateLib from "@/Utils/StateLib";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  Select,
  Modal,
  Input,
  SearchMenu,
  List,
  Checkbox,
  TextArea
} from "@theorgchartdev/storybook";
import { SelectableSearchMenu } from "../../Menus/BasicChartSearchMenu/SelectableSearchMenu";
import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import { appstr } from "@coreapp/utils/appstr";
import { appstrp } from "@coreapp/utils/appstrp";
import { getCurrentChartName } from "@/Utils/Misc";
import { ListItemOption } from "@theorgchartdev/storybook/dist/components/List/ListItem";
import cx from "classnames";
import { ShareWithHandler } from "./SharedWithHandler";
import {
  PlanObjectiveColors,
  ObjectiveHandler,
  planObjectiveHelpLink
} from "@/Utils/Plan/ObjectivesHandler";
import LevelHandler from "@/Utils/Chart/LevelHandler";
import { planFormReducer, PlanFormState } from "@/Utils/Plan/NewPlanForm";
import { BoxChip } from "@/Utils/BoxChip";
import { getTopOfChartFunc } from "@/Utils/Chart/NewDocForm";

type PlanBuilderModalProps = {
  show: boolean;
  onClose: () => void;
  onCreate: (formState: PlanFormState) => void;
};

const PlanBuilderModal = (props: PlanBuilderModalProps) => {
  const { show, onClose, onCreate } = props;
  const currentChartName = getCurrentChartName();

  if (!show) {
    return null;
  }

  // Form state
  const [formState, dispatch] = useReducer(planFormReducer, {
    name: appstr("Chart plan", { chart: currentChartName }),
    description: "",
    levels: -1,
    topbox: undefined,
    objective: null,
    isDirty: false,
    sharedWith: [],
    emailInvite: false
  });

  const [shareHandler, setShareHandler] = useState<Nullable<ShareWithHandler>>(null);
  const [sharedQuery, setSharedQuery] = useState<Nullable<string>>(null);

  useEffect(() => {
    const asyncSharedHandler = async () => {
      const shareHandler = new ShareWithHandler();
      await shareHandler.loadUsersAndGroups();

      shareHandler.buildUserAndGroupOptions([], (users: any[]) => {
        dispatch({
          type: "SET_SHARED_WITH",
          payload: users,
          clearEmailInvite: true
        });
      });
      setShareHandler(shareHandler);
    };

    asyncSharedHandler();
    getTopOfChart();
  }, []);

  const getTopOfChart = getTopOfChartFunc(dispatch);

  const handleClose = () => {
    onClose();
  };

  const isFormValid = () => {
    return (
      formState.name.length > 0 &&
      formState.levels !== null &&
      formState.objective !== null &&
      formState.topbox !== null
    );
  };

  const handleCreate = () => {
    onCreate(formState);
  };

  const isPosition = StateLib.GetDoc()?.IsPositionDocument();
  const baseClass = "ocn-builder";
  const levelOptions = LevelHandler.buildLevelOptions((level: ListItemOption) =>
    dispatch({ type: "SET_LEVELS", payload: level.value })
  );
  const objectiveOptions = ObjectiveHandler.buildObjectiveOptions((objective: ListItemOption) =>
    dispatch({ type: "SET_OBJECTIVE", payload: objective.value })
  );
  const sharedUserOptions =
    shareHandler?.getShareWithOptions(sharedQuery || "", formState.sharedWith) ?? [];
  const sharedUserList =
    shareHandler?.buildActiveSharedUserOptions(
      formState.sharedWith,
      (username: string, role: string) =>
        dispatch({ type: "SET_SHARED_WITH_ROLE", payload: { username, role } }),
      (username: string) => dispatch({ type: "REMOVE_SHARED_WITH", payload: username })
    ) ?? [];

  const requiredTicker = <span className={"ocn-form-label-required"}> *</span>;

  return (
    <CentralizedModalContainer show={show}>
      <Modal
        id="builder-card"
        show={show}
        centerScreen={true}
        onClose={handleClose}
        fullWidth={true}
        customStyle={{ padding: 0, width: "600px" }}
        className="ocn-plan-builder-modal ocn-chart-doc-builder-modal"
      >
        <DialogTitle divider={true} onClose={handleClose}>
          <span className={"ocn-explorer-icon-label"}>
            <Icon size={"medium"} name={"planning"} />
            {appstrp("Create plan from", `: ${currentChartName}`)}
          </span>
        </DialogTitle>

        {/* PLAN NAME */}
        <DialogContent dividers={false}>
          <Input
            id={"builder-name-input"}
            key={"name-plan-input"}
            label={appstr("Entity name", { entity: appstr("Plan") })}
            placeholder={appstr("Enter Plan Name")}
            value={formState.name}
            size={"medium"}
            onChange={e =>
              dispatch({ type: "SET_NAME", payload: (e.target as HTMLInputElement).value })
            }
            required
          />
        </DialogContent>

        {/* PLAN DESCRIPTION */}
        <DialogContent dividers={false}>
          <TextArea
            id={"builder-description-input"}
            key={"description-plan-input"}
            label={appstr("Description")}
            placeholder={appstr("Add a description")}
            value={formState.description}
            charLimit={1000}
            onChange={e =>
              dispatch({
                type: "SET_DESCRIPTION",
                payload: (e.target as HTMLTextAreaElement).value
              })
            }
          />
        </DialogContent>

        {/* PLAN TOPBOX */}
        <DialogContent dividers={false}>
          <div className="ocn-builder">
            <label
              className="ocn-form-label ocn-form-input-label-vertical-gap"
              htmlFor="builder-topbox-input"
            >
              {appstr(isPosition ? "Build Position Doc" : "Build Employees Plan", {
                document: appstr("Plan").toLowerCase()
              })}
              {requiredTicker}
            </label>
            <div className={"ocn-builder-search"}>
              <SelectableSearchMenu
                dataTestId="plan-topbox-search"
                inputProps={{ id: "builder-topbox-input" }}
                inputSize="medium"
                listWidth="fill"
                displayRecents={false}
                selectedItem={formState.topbox}
                onRemoveItem={() => dispatch({ type: "SET_TOPBOX", payload: null })}
                searchMenuProps={{
                  clearOnSelect: true,
                  clearable: true,
                  placeholder: formState.topbox ? "" : appstr("Search by name or title")
                }}
                handleSearchOptionClick={(target: any) => {
                  const lookupRecordObj = BoxChip.FromSearchResult(
                    target.result,
                    false,
                    StateLib.GetDoc()
                  );
                  dispatch({
                    type: "SET_TOPBOX",
                    payload: lookupRecordObj,
                    isPosition
                  });
                }}
                queryOptions={{ includeAux: false }}
              />
            </div>
          </div>
        </DialogContent>

        {/* PLAN LEVELS */}
        <DialogContent dividers={false}>
          <span className={baseClass}>
            <label
              className="ocn-form-label ocn-form-input-label-vertical-gap"
              htmlFor="builder-levels-select"
            >
              {appstr(isPosition ? "Select Build Position Level" : "Select Build Chart Level")}
              {requiredTicker}
            </label>
            <div className={"ocn-builder-search"}>
              <span className={"ocn-builder-edit-text"}>
                <Select
                  id="builder-levels-select"
                  dataTestId={"builder-lvls-menu"}
                  optionSize="medium"
                  selectSize="medium"
                  value={formState.levels}
                  options={levelOptions}
                />
              </span>
            </div>
          </span>
        </DialogContent>

        {/* PLAN OBJECTIVE */}
        <DialogContent dividers={false}>
          <span className={baseClass}>
            <label
              className="ocn-form-label ocn-form-input-label-vertical-gap"
              htmlFor="builder-objective-select"
            >
              {appstr("Plan Objective")}
              {requiredTicker}
            </label>
            <div className={"ocn-builder-search"}>
              <span className={"ocn-builder-edit-text"}>
                <Select
                  id="builder-objective-select"
                  dataTestId="builder-objective-menu"
                  optionSize="medium"
                  selectSize="medium"
                  placeholder={formState.objective ? "" : appstr("Select objective")}
                  value={formState.objective}
                  options={objectiveOptions}
                  addPill
                  getPillColor={value => PlanObjectiveColors[value]}
                  onRemoveOption={(_, e) => {
                    e.stopPropagation();
                    dispatch({ type: "SET_OBJECTIVE", payload: null });
                  }}
                />
              </span>
            </div>
            <span className={"ocn-builder-help"}>
              {appstr("Plan Objective Help") + " "}
              <a href={planObjectiveHelpLink} target="_blank" rel="noreferrer">
                {appstr("Plan Objective Help Link")}
              </a>
              .
            </span>
          </span>
        </DialogContent>

        {/* PLAN SHARED WITH */}
        <DialogContent dividers={false}>
          <span className={baseClass}>
            <label className="ocn-form-label ocn-form-input-label-vertical-gap">
              {appstr("Share this plan with")} <span>({appstr("this can be done later")})</span>
            </label>
            <SearchMenu
              placeholder={appstr("Add people or groups")}
              searchSize={"medium"}
              options={sharedUserOptions}
              optionSize={"medium"}
              searchCallback={(query: string) => setSharedQuery(query)}
              loading={false}
              defaultInputVal={""}
            />
          </span>
          {formState.sharedWith.length > 0 && (
            <div className={cx("oc-list-container", "ocn-list-scrollbar", "ocn-plan-shared--list")}>
              <Checkbox
                checked={formState.emailInvite}
                id="notify-users-share-plan"
                indeterminate={false}
                label={appstr("Notify people")}
                onToggle={(e: ChangeEvent<HTMLInputElement>) => {
                  dispatch({ type: "SET_EMAIL_INVITE", payload: e?.target?.checked });
                }}
                size="medium"
              />
              <List
                enableVirtualization={false}
                listWidth="fill"
                options={sharedUserList}
                showCheck={false}
              />
            </div>
          )}
        </DialogContent>

        {/* ACTIONS */}
        <DialogActions>
          <DialogActions.Tertiary onClick={handleClose} size="medium">
            {appstr("Cancel")}
          </DialogActions.Tertiary>
          <DialogActions.Primary
            onClick={handleCreate}
            dataTestId={"builder-create-plan-btn"}
            size="medium"
            disabled={!formState.isDirty || !isFormValid()}
          >
            {appstr("Create")}
          </DialogActions.Primary>
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};

export default PlanBuilderModal;
